import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Modal } from './Modal';
import { Button } from './Button';

/**
 * FormModal Component
 *
 * Reusable modal wrapper for create/edit forms.
 * Provides standard header with title and body with form fields and action buttons.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {string} props.title - Modal title
 * @param {boolean} props.isLoading - Whether the save action is in progress
 * @param {string} props.submitText - Text for submit button (default: "Save")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {React.ReactNode} props.children - Form content (children)
 * @param {string} props.size - Modal size: 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param {boolean} props.isValid - Whether form is valid (enables submit button)
 * @param {string} props.bodyClassName - Additional CSS classes for modal body
 */
export function FormModal({
  open,
  onClose,
  onSubmit,
  title,
  isLoading = false,
  submitText = 'Save',
  cancelText = 'Cancel',
  children,
  size = 'md',
  isValid = true,
  bodyClassName = '',
}) {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const footer = (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={onClose}
        disabled={isLoading}
      >
        {cancelText}
      </Button>
      <Button
        type="submit"
        variant="primary"
        disabled={!isValid || isLoading}
        icon={isLoading ? <Icon icon="eos-icons:loading" /> : null}
        onClick={handleSubmit}
      >
        {isLoading ? 'Creating...' : submitText}
      </Button>
    </>
  );

  return (
    <Modal isOpen={open} onClose={onClose} title={title} size={size} footer={footer} bodyClassName={bodyClassName}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Form Fields */}
        {children}
      </form>
    </Modal>
  );
}

FormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  submitText: PropTypes.string,
  cancelText: PropTypes.string,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  isValid: PropTypes.bool,
  bodyClassName: PropTypes.string,
};
